import { CheckCircle2 } from 'lucide-react'
import { useEffect, useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import type { TenantResponse } from '@/api/responses'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
} from '@/components/ui'
import { useUpdateWebsite } from '@/features/knowledge/queries'
import { translateEnumValue } from '@/lib/formatting'

import { SettingRow } from './SettingRow'

/** Quanto tempo a confirmação de "Site atualizado" fica visível antes de sumir sozinha. */
const SAVED_FEEDBACK_MS = 4_000

export interface OrganizationCardProps {
  readonly tenant: TenantResponse
}

/**
 * Card da Organização: nome, identificador e status são read-only (o backend não permite editá-los
 * hoje). O único campo editável é o site da empresa, salvo pelo mesmo fluxo do Conhecimento
 * (`useUpdateWebsite` → PATCH /tenants/{id} com `{ website }`), com feedback de sucesso discreto e
 * tratamento do erro `invalid_website`. Vocabulário neutro; nunca expõe infraestrutura.
 */
export function OrganizationCard({ tenant }: OrganizationCardProps) {
  const { t } = useTranslation()
  const mutation = useUpdateWebsite(tenant.id)

  const [website, setWebsite] = useState(tenant.website ?? '')
  const [showSaved, setShowSaved] = useState(false)

  // Confirmação de sucesso some sozinha após alguns segundos (feedback discreto, não persistente).
  useEffect(() => {
    if (!showSaved) {
      return
    }
    const timer = setTimeout(() => {
      setShowSaved(false)
    }, SAVED_FEEDBACK_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [showSaved])

  const invalidWebsite =
    mutation.error instanceof ApiError && mutation.error.problem.errorCode === 'invalid_website'
  const genericError = mutation.isError && !invalidWebsite

  const trimmed = website.trim()
  const unchanged = trimmed === (tenant.website ?? '')
  const canSave = trimmed.length > 0 && !unchanged

  const statusLabels: Readonly<Record<string, string>> = {
    Provisioning: t('settings.organization.statuses.Provisioning'),
    Active: t('settings.organization.statuses.Active'),
    Suspended: t('settings.organization.statuses.Suspended'),
    Disabled: t('settings.organization.statuses.Disabled'),
    Deleted: t('settings.organization.statuses.Deleted'),
  }

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSave || mutation.isPending) {
      return
    }
    setShowSaved(false)
    mutation.mutate(trimmed, {
      onSuccess: () => {
        setShowSaved(true)
      },
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.organization.title')}</CardTitle>
        <CardDescription>{t('settings.organization.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="flex flex-col gap-3 text-sm">
          <SettingRow label={t('settings.organization.nameLabel')} value={tenant.name} />
          <SettingRow label={t('settings.organization.slugLabel')} value={tenant.slug} />
          <div className="flex items-center justify-between gap-4">
            <span className="text-foreground-muted">{t('settings.organization.statusLabel')}</span>
            <Badge variant={tenant.status === 'Active' ? 'success' : 'neutral'}>
              {translateEnumValue(statusLabels, tenant.status)}
            </Badge>
          </div>
        </div>

        <form
          onSubmit={(event) => {
            submit(event)
          }}
          className="flex flex-col gap-3 border-t border-border-default pt-5"
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <Input
              id="settings-website"
              label={t('settings.organization.website.label')}
              placeholder={t('settings.organization.website.placeholder')}
              hint={t('settings.organization.website.hint')}
              value={website}
              onChange={(event) => {
                setWebsite(event.target.value)
                setShowSaved(false)
                if (mutation.isError) {
                  mutation.reset()
                }
              }}
              error={invalidWebsite ? t('settings.organization.website.invalid') : undefined}
              className="sm:flex-1"
            />
            <Button type="submit" loading={mutation.isPending} disabled={!canSave}>
              {t('settings.organization.website.save')}
            </Button>
          </div>

          {showSaved ? (
            <p role="status" className="flex items-center gap-1.5 text-sm text-status-success">
              <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
              {t('settings.organization.website.saved')}
            </p>
          ) : null}

          {genericError ? (
            <Alert variant="danger" title={t('settings.organization.website.error')} />
          ) : null}
        </form>
      </CardContent>
    </Card>
  )
}
